import { apiClient } from '../config/api-config';
import type { AuthResponse, LoginResponse } from '../schemas/auth-schemas';
import { i18n } from '@tasama/i18n';

interface ValidateTokenResponse {
  email: string;
  type: 'reset' | 'activation' | 'forgot';
  valid: boolean;
}

// ApiResponse type for backward compatibility
export interface ApiResponse<T> {
  data: T;
  message?: string;
  status: 'success' | 'error';
}

/**
 * Account Management API service functions
 * Handles account activation, password reset, reactivation, and related operations
 */

// Account Management API services
export const accountManagementService = {
  // Request account reactivation email
  requestReactivation: async (
    token: string
  ): Promise<ApiResponse<{ message: string }>> => {
    try {
      const response = await apiClient.axios.post<{ message: string }>(
        '/account-management/request-reactivation',
        { token }
      );
      return {
        data: response.data,
        status: 'success',
        message: 'Reactivation request sent successfully',
      };
    } catch (error: any) {
      return {
        data: null as any,
        status: 'error',
        message:
          error?.response?.data?.message ||
          'Failed to send reactivation request',
      };
    }
  },

  // Request password reset email
  requestResetPassword: async (
    token: string
  ): Promise<ApiResponse<{ message: string }>> => {
    try {
      const response = await apiClient.axios.post<{ message: string }>(
        '/account-management/request-reset-password',
        { token }
      );
      return {
        data: response.data,
        status: 'success',
        message: 'Password reset request sent successfully',
      };
    } catch (error: any) {
      return {
        data: null as any,
        status: 'error',
        message:
          error?.response?.data?.message ||
          'Failed to send password reset request',
      };
    }
  },

  // Request forgot password email
  requestForgotPassword: async (
    email: string
  ): Promise<ApiResponse<{ message: string }>> => {
    try {
      const response = await apiClient.axios.post<{ message: string }>(
        '/account-management/request-forgot-password',
        { email }
      );
      return {
        data: response.data,
        status: 'success',
        message: 'Password reset email sent successfully',
      };
    } catch (error: any) {
      // Handle rate limiting (429 Too Many Requests)
      if (error?.response?.status === 429) {
        return {
          data: null as any,
          status: 'error',
          message: i18n.t('tooManyPasswordResets', { ns: 'auth' }),
        };
      }

      return {
        data: null as any,
        status: 'error',
        message:
          error?.response?.data?.message ||
          'Failed to send forgot password email',
      };
    }
  },

  // Activate user account — BFF sets httpOnly cookies on success.
  // Returns user info from the response body for immediate store population.
  // When BFF auto-login fails, response includes requiresManualLogin: true and null user.
  activateAccount: async (
    token: string,
    password: string
  ): Promise<ApiResponse<AuthResponse & { requiresManualLogin?: boolean }>> => {
    try {
      const response = await apiClient.axios.post<
        LoginResponse & { requiresManualLogin?: boolean; message?: string }
      >('/account-management/activate-account', { token, password });

      if (response.data.requiresManualLogin) {
        return {
          data: { requiresManualLogin: true } as any,
          status: 'success',
          message: response.data.message || 'Account activated successfully',
        };
      }

      const transformedData: AuthResponse & { requiresManualLogin?: boolean } =
        {
          user: {
            id: response.data.user.sub,
            email: response.data.user.email,
            username: response.data.user.username,
            isAdmin: response.data.user.isAdmin || false,
          },
          sessionFingerprint: response.data.session_fingerprint,
        };

      return {
        data: transformedData,
        status: 'success',
        message: 'Account activated successfully',
      };
    } catch (error: any) {
      return {
        data: null as any,
        status: 'error',
        message: error?.response?.data?.message || 'Failed to activate account',
      };
    }
  },

  // Reset password — BFF sets httpOnly cookies on success.
  // Returns user info from the response body for immediate store population.
  resetPassword: async (
    token: string,
    password: string
  ): Promise<ApiResponse<AuthResponse>> => {
    try {
      const response = await apiClient.axios.post<LoginResponse>(
        '/account-management/reset-password',
        { token, password }
      );

      const transformedData: AuthResponse = {
        user: {
          id: response.data.user.sub,
          email: response.data.user.email,
          username: response.data.user.username,
          isAdmin: response.data.user.isAdmin || false,
        },
        sessionFingerprint: response.data.session_fingerprint,
      };

      return {
        data: transformedData,
        status: 'success',
        message: 'Password reset successfully',
      };
    } catch (error: any) {
      return {
        data: null as any,
        status: 'error',
        message: error?.response?.data?.message || 'Failed to reset password',
      };
    }
  },

  validateToken: async (
    token: string,
    currentPath?: string
  ): Promise<ValidateTokenResponse> => {
    try {
      const response = await apiClient.axios.post<ValidateTokenResponse>(
        '/account-management/validate-token',
        { token }
      );

      // Return the direct response data from axios
      return response.data;
    } catch (error: any) {
      // Extract status from axios error structure
      const status = error?.response?.status || error?.code;

      if (status === 401) {
        // Read type from BFF error body first; fall back to path inference
        const validTypes = ['reset', 'activation', 'forgot'] as const;
        const bffType = error?.response?.data?.type;
        const tokenType: 'reset' | 'activation' | 'forgot' =
          validTypes.includes(bffType)
            ? bffType
            : currentPath?.includes('activate')
              ? 'activation'
              : currentPath?.includes('reset-password')
                ? 'reset'
                : 'forgot';

        return {
          email: error?.response?.data?.email || '',
          type: tokenType,
          valid: false,
        };
      }

      // Re-throw other errors to be handled by the router loader
      throw error;
    }
  },
};

export default accountManagementService;
